import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import GroupPicker from '../components/GroupPicker';

export default function DocumentEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', keywords: '', validity_type: 'none',
    validity_value: '', is_restricted: false, allow_printing: false, access_groups: [],
  });
  const [file, setFile] = useState(null);
  const [changeLog, setChangeLog] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/documents/${id}`).then((d) => {
      const data = d.data;
      setDoc(data);
      setForm({
        name: data.name || '',
        description: data.description || '',
        keywords: data.keywords || '',
        validity_type: data.validity_type || 'none',
        validity_value: data.validity_value ?? '',
        is_restricted: Boolean(data.is_restricted),
        allow_printing: Boolean(data.allow_printing),
        access_groups: (data.access_groups || []).map((ag) => ag.id),
      });
    }).catch((err) => {
      setError(err.response?.data?.error || 'Erro ao carregar documento');
    }).finally(() => setLoading(false));
  }, [id]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    // O backend exige log de alteracao junto de arquivo novo; validar aqui
    // evita perder o formulario preenchido num 400.
    if (file && !changeLog.trim()) {
      setError('Log de alteração e obrigatório ao enviar um novo arquivo');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('description', form.description);
      formData.append('keywords', form.keywords);
      formData.append('validity_type', form.validity_type);
      // Sem validade nao ha valor: mandar string vazia gravaria lixo
      if (form.validity_type !== 'none' && form.validity_value !== '') {
        formData.append('validity_value', form.validity_value);
      } else {
        formData.append('validity_value', '');
      }
      formData.append('is_restricted', form.is_restricted);
      formData.append('allow_printing', form.allow_printing);
      formData.append('access_groups', JSON.stringify(form.is_restricted ? form.access_groups : []));
      if (file) {
        formData.append('file', file);
        formData.append('change_log', changeLog);
      }

      await api.put(`/documents/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      navigate(`/documents/${id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar documento');
    }
    setSaving(false);
  }

  if (loading) return <div className="flex items-center justify-center py-12 text-ink-muted">Carregando...</div>;
  if (!doc) return <div className="p-3 rounded-md text-sm bg-status-rejected/10 text-status-rejected border border-status-rejected/20">{error}</div>;

  const version = doc.version;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <span className="doc-code">{doc.code}</span>
        <h1 className="text-xl font-bold tracking-tight">Editar Documento</h1>
      </div>

      <div className="bg-surface-100 border rounded-md p-6 max-w-[700px]">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="p-3 rounded-md text-sm mb-4 bg-status-rejected/10 text-status-rejected border border-status-rejected/20">{error}</div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">Nome *</label>
            <input className="form-input" name="name" value={form.name} onChange={handleChange} required />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">Descrição</label>
            <textarea className="form-textarea" name="description" value={form.description} onChange={handleChange} />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">Palavras-chave (separadas por virgula)</label>
            <input className="form-input" name="keywords" value={form.keywords} onChange={handleChange} />
          </div>

          {/* Pasta, departamento, categoria e processo compoem o codigo do
              documento, que ja foi emitido. Alterar aqui invalidaria o codigo,
              entao aparecem apenas como referencia. */}
          <div className="mb-4 p-3 bg-surface-inset rounded text-sm">
            <div className="text-xs font-semibold text-ink-muted mb-2">Classificacao (define o código, não editável)</div>
            <div className="grid grid-cols-2 gap-y-1 text-ink-secondary">
              <div>Pasta: <span className="text-ink">{doc.folder?.name || '—'}</span></div>
              <div>Departamento: <span className="text-ink">{doc.department?.name || '—'}</span></div>
              <div>Categoria: <span className="text-ink">{doc.category?.name || '—'}</span></div>
              <div>Processo: <span className="text-ink">{doc.process?.name || '—'}</span></div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-secondary mb-1">Tipo de Validade</label>
              <select className="form-select" name="validity_type" value={form.validity_type} onChange={handleChange}>
                <option value="none">Sem validade</option>
                <option value="months">Meses</option>
                <option value="years">Anos</option>
              </select>
            </div>
            {form.validity_type !== 'none' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-ink-secondary mb-1">Valor</label>
                <input className="form-input" type="number" name="validity_value" value={form.validity_value} onChange={handleChange} min="1" />
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">
              Substituir arquivo (opcional)
            </label>
            <div className="text-xs text-ink-muted mb-2">
              Atual: {version?.file_name || '—'} — versao {version?.version_number}.{version?.sub_version}.
              Enviar um arquivo novo cria uma nova subversao.
            </div>
            <input type="file" className="form-input" onChange={(e) => setFile(e.target.files[0] || null)} />
          </div>

          {file && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-secondary mb-1">Log de alteração *</label>
              <textarea className="form-textarea" value={changeLog} onChange={(e) => setChangeLog(e.target.value)}
                placeholder="Descreva o que mudou nesta versão" required />
            </div>
          )}

          <div className="flex gap-4 mb-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="is_restricted" checked={form.is_restricted} onChange={handleChange} />
              Acesso Restrito
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="allow_printing" checked={form.allow_printing} onChange={handleChange} />
              Permitir Impressão
            </label>
          </div>

          {form.is_restricted && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-secondary mb-1">Grupos de Acesso</label>
              <GroupPicker
                value={form.access_groups}
                onChange={(access_groups) => setForm((prev) => ({ ...prev, access_groups }))}
              />
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button type="button" className="btn btn-secondary" onClick={() => navigate(`/documents/${id}`)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
